import { useQuery } from "@tanstack/react-query";
import type { CameraStatusResponse } from "../types/cameraApi";
import { apiGet } from "../lib/api";

/** 카메라 화각 데이터는 하루 1회(현장 GPU 06:10 push)라 폴링을 길게 잡는다. */
const STALE_TIME_MS = 5 * 60_000; // 5분 — 그 안에는 캐시를 신선하게 본다.
const REFETCH_INTERVAL_MS = 5 * 60_000; // 5분마다 조용히 재조회.

/**
 * 카메라 화각(FOV) 변화 감지 상태를 백엔드에서 폴링한다.
 *
 * <p>{@code GET /api/camera/status} — 최근 90일 윈도우를 사이트→카메라로 그룹핑한
 * 스냅샷. 데이터 없으면 백엔드가 {@code { thresholdPx, sites: [] }} (200) 를 주므로
 * 빈 상태는 정상 처리(에러 아님).</p>
 *
 * <p>실시간 대시보드(`useStatus` 30s)와 달리 이 데이터는 하루 1회만 갱신되므로
 * staleTime/refetchInterval 을 5분으로 크게 잡아 불필요한 트래픽을 줄인다.
 * 일시적 네트워크 실패 시에도 마지막 성공 데이터를 유지한다(placeholderData: prev).</p>
 */
export function useCameraStatus() {
  return useQuery<CameraStatusResponse>({
    queryKey: ["camera-status"],
    queryFn: ({ signal }) =>
      apiGet<CameraStatusResponse>(
        "/api/camera/status",
        signal ? { signal } : undefined,
      ),
    staleTime: STALE_TIME_MS,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    placeholderData: (prev) => prev,
  });
}
